/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AccessListService } from '../../services/access-list.service';
import { UserService } from '../../services/user.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs/internal/Subscription';
import {
  BulkInvitaionComponent,
  InvitaionReport,
} from './invite-multip-users.modal.component';
import { AccountService } from '../../services/account.service';
import { Constants, Language } from '../../../common/constants';
import { Workflow, WorkflowService } from '../../services/workflow.service';
import { ErrorService } from '../../services/error.service';
import { EmailService } from '../../services/email.service';

@Component({
  selector: 'invite-user',
  templateUrl: './invite-user.component.html',
  styleUrls: ['./invite-user.component.less'],
  standalone: false,
})
export class InviteUserComponent implements OnInit, OnDestroy {
  email: string;
  error: string;
  appId: string;
  processing = false;
  refreshSub: Subscription;
  selectedLanguage: Language;
  languages: Language[];
  prefill = false;
  enablePrefill = false;
  orphanedWorkflows: any;

  csvContent: string;
  fileIsValid = false;
  maxEmails = 100;
  parsedEmails: string[] = [];
  badEmails: string[] = [];
  duplicatedEmails: string[] = [];
  usersWithOtherSubscription: string[] = [];
  usersWithThisSubscription: string[] = [];
  usersAlreadyInvited: string[] = [];
  usersSuspended: string[] = [];
  usersRejected: string[] = [];
  usersLocked: string[] = [];
  usersDeleted: string[] = [];
  otherInvalidEmails: string[] = [];

  input: {
    uploadFile: File | null;
    fileTypes: { asAccept: string | null; asString: string | null };
  } = {
    uploadFile: null,
    fileTypes: {
      asAccept: null,
      asString: null,
    },
  };

  regexp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );

  constructor(
    private accessListService: AccessListService,
    private userService: UserService,
    private xdaStringsService: XdaStringsService,
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private accountService: AccountService,
    private workflowService: WorkflowService,
    private errorService: ErrorService,
    private emailService: EmailService,
  ) {
    this.appId = this.xdaStringsService.strings['APPID'];
    this.enablePrefill = this.prefill =
      this.userService.user?.bvdEntityType == 'Orbis' ||
      this.userService.user?.bvdEntityType == 'OwnCompany'
        ? true
        : false;
  }

  ngOnInit() {
    this.languages = Constants.languages;
    const browserLanguage =
      navigator.language === 'pt' ? 'pt-br' : navigator.language;
    this.selectedLanguage =
      this.languages.find((x) => x.ISOCode === browserLanguage.toLowerCase()) ||
      this.languages.find((x) => x.ISOCode === 'en-us')!;
    this.refreshSub = this.accessListService.refreshAccessListSuccess.subscribe(
      () => {
        this.activeModal.dismiss();
        this.processing = false;
      },
    );
    this.setupInput();
  }

  setupInput() {
    this.input.fileTypes.asAccept = '.csv';
    this.input.fileTypes.asString = 'csv';
  }

  isMultiInvite() {
    let multiInvite = false;
    let emailStr = this.email ?? '';
    // remove the last separator
    if (
      emailStr.endsWith(' ') ||
      emailStr.endsWith(';') ||
      emailStr.endsWith(',')
    ) {
      emailStr = emailStr.slice(0, -1);
    }

    const arr = emailStr.split(/[ ,;]+/);
    if (this.email && arr.length === 1) {
      if (this.parsedEmails.length) {
        // include the entered email address
        this.parsedEmails.push(this.email.trim());
        multiInvite = true;
      }
    } else {
      arr.forEach((em) => {
        if (em !== '') {
          this.parsedEmails.push(em.trim());
        }
      });
      multiInvite = true;
    }

    if (multiInvite) {
      // check for duplicates
      const unique = [];
      let uIndex = 0;
      this.parsedEmails.sort();
      unique.push(this.parsedEmails[0]);
      for (let i = 1; i < this.parsedEmails.length; i++) {
        if (unique[uIndex] === this.parsedEmails[i]) {
          this.duplicatedEmails.push(this.parsedEmails[i]);
        } else {
          unique.push(this.parsedEmails[i]);
          uIndex++;
        }
      }

      this.parsedEmails = unique;
    }

    return multiInvite;
  }

  async canInvite(f: NgForm) {
    if (this.isMultiInvite()) {
      this.activeModal.dismiss();
      // open bulk invitation info modal
      const modalRef = this.modalService.open(BulkInvitaionComponent);
      const report: InvitaionReport = {
        badEmails: this.badEmails,
        duplicatedEmails: this.duplicatedEmails,
        usersWithOtherSubscription: this.usersWithOtherSubscription,
        usersWithThisSubscription: this.usersWithThisSubscription,
        usersAlreadyInvited: this.usersAlreadyInvited,
        usersSuspended: this.usersSuspended,
        usersRejected: this.usersRejected,
        usersLocked: this.usersLocked,
        usersDeleted: this.usersDeleted,
        otherInvalidEmails: this.otherInvalidEmails,
      };
      modalRef.componentInstance.report = report;
      modalRef.componentInstance.badEmails = this.badEmails;
      modalRef.componentInstance.usersWithOtherSubscription =
        this.usersWithOtherSubscription;
      modalRef.componentInstance.usersWithThisSubscription =
        this.usersWithThisSubscription;
      modalRef.componentInstance.usersAlreadyInvited = this.usersAlreadyInvited;
      modalRef.componentInstance.otherInvalidEmails = this.otherInvalidEmails;
      await this.bulkInvite(modalRef);
    }
    // invite single user
    else {
      this.error = '';
      // check the email format since the input type is not email, xrx-input is not checking the email format
      if (this.email && !this.emailService.isValidEmailAddress(this.email)) {
        setTimeout(() => {
          this.error = 'SDE_EMAIL_NOT_VALID';
        });
        f.form.get('email')?.setErrors({ customerror: true });
        return;
      }
      try {
        const res = await this.accessListService.canInviteUser(
          f.value.email,
          this.userService.user?.accessListId || '',
          this.appId,
        );
        this.addToAccessList(res.toString());
      } catch (error) {
        try {
          if ((error as { error: string }).error !== undefined) {
            const e = error as { error: string };
            const obj = JSON.parse(e.error);

            if (!obj) {
              this.error = 'SDE_USERS_DELETED_CANNOT';
              f.form.get('email')?.setErrors({ customerror: true });
            } else if (obj.message === 'New User') {
              this.activeModal.dismiss();
              this.invite(this.email);
            } else {
              this.error = obj.message;
              f.form.get('email')?.setErrors({ customerror: true });
            }
          }
        } catch (error) {
          this.errorService.displayGeneralError().result.then(() => {
            this.activeModal.dismiss();
          });
        }
      }
    }
  }

  async bulkInvite(modalRef: NgbModalRef) {
    // yield to let modal completes its initialization
    setTimeout(async () => {
      const stepPercentage = modalRef.componentInstance.initProgress(
        this.parsedEmails.length,
      );
      this.parsedEmails.forEach(async (email) => {
        if (this.emailService.isValidEmailAddress(email)) {
          await this.inviteUser(email);
        } else {
          this.badEmails.push(email);
        }
        // move the progress bar forward
        modalRef.componentInstance.setProgressPercentage(stepPercentage);
      });

      //last the step in case of not reaching 100% due to rounding error.
      modalRef.componentInstance.setProgressPercentage(stepPercentage);
    }, 1);
  }

  async inviteUser(email: string) {
    try {
      const res = await this.accessListService.canInviteUser(
        email,
        this.userService.user?.accessListId || '',
        this.appId,
      );
      this.addToAccessList(res.toString());
    } catch (error) {
      try {
        if ((error as { error: string }).error !== undefined) {
          const e = error as { error: string };
          const obj = JSON.parse(e.error);

          if (!obj) {
            this.usersDeleted.push(email);
          } else if (obj.message === 'New User') {
            await this.invite(email);
          } else if (
            obj.message.indexOf('associated with this subscription') > 0
          ) {
            this.usersWithThisSubscription.push(email);
          } else if (
            obj.message.indexOf('associated with another subscription') > 0
          ) {
            this.usersWithOtherSubscription.push(email);
          } else if (obj.message.indexOf('already been invited') > 0) {
            this.usersAlreadyInvited.push(email);
          } else if (obj.message.indexOf('suspended') > 0) {
            this.usersSuspended.push(email);
          } else if (obj.message.indexOf('rejected') > 0) {
            this.usersRejected.push(email);
          } else if (obj.message.indexOf('locked') > 0) {
            this.usersLocked.push(email);
          } else {
            console.error('Invite User Error:' + email + '::' + obj.message);
            this.otherInvalidEmails.push(email);
          }
        }
      } catch (error) {
        this.errorService.displayGeneralError().result.then(() => {
          this.activeModal.dismiss();
        });
      }
    }
  }

  async invite(email: string) {
    this.error = '';
    try {
      const res = await this.accountService.inviteUser(
        email,
        this.appId,
        this.selectedLanguage.ISOCode,
        String(this.prefill),
      );
      this.addToAccessList(res.toString());
    } catch (error) {
      console.log(error);
      this.errorService.displayGeneralError().result.then(() => {
        this.activeModal.dismiss();
      });
    }
  }

  async addToAccessList(userId: string) {
    this.processing = true;
    const accessListItem: AccessListItem = {
      accessListId: this.userService.user?.accessListId || '',
      userId: userId,
      appId: this.appId,
      managerId: this.userService.user?.id || '',
      accessListRole: 'User',
    };
    try {
      await this.accessListService.addToAccessList(accessListItem);
      this.restoreOrphanWorkflowsForUser(userId);
      this.accessListService.emitRefreshEvent();
    } catch (error) {
      console.log(error);
      this.errorService.displayGeneralError().result.then(() => {
        this.activeModal.dismiss();
      });
    }
  }

  restoreOrphanWorkflowsForUser(userId: string) {
    this.orphanedWorkflows = this.workflowService.workflows.filter(
      (x) => x.creator == userId && x.isOrphan,
    );
    this.orphanedWorkflows.forEach((workflow: Workflow) => {
      this.workflowService.setWorkflowOrphanFlag(workflow, false).subscribe(
        () => {},
        (error) => {
          console.log(error);
        },
      );
    });
  }

  async handleUploadFileInput(prams: { files: FileList; isValid: boolean }) {
    this.input.uploadFile = prams.files.item(0) || null;
    this.fileIsValid = prams.isValid;

    // parse CSV file
    await this.emailService
      .parseCSVFile(this.input.uploadFile, this.maxEmails)
      .then((emails) => {
        this.parsedEmails = emails[0];
        this.csvContent = emails[1]?.toString() || '';
        if (emails[2] && emails[2].length > 0) {
          emails[2].forEach((d) => {
            this.duplicatedEmails.push(d);
          });
        }
      })
      .catch((e) => {
        console.log(e);
      });
  }

  onKeyup() {
    this.email = this.emailService.cleanAddressList(this.email);
  }

  ngOnDestroy() {
    this.refreshSub.unsubscribe();
  }
}

export interface AccessListItem {
  accessListId: string;
  userId: string;
  appId: string;
  managerId: string;
  accessListRole: string;
}
